import {
  Alert,
  Button,
  Modal,
  PageHeader,
  Skeleton,
  StatusBadge,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import {
  ClipboardList,
  Download,
  FileSignature,
  PenLine,
  Send,
  Truck,
  Upload,
  XCircle,
} from 'lucide-react';
import { type JSX, useState } from 'react';

import { ApiError } from '../../api/client';
import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import {
  dispatchDeliveryNote,
  downloadDeliveryNotePdf,
  getDeliveryNote,
  markDeliveryNoteDelivered,
  voidDeliveryNote,
} from '../../delivery-notes/api';
import { mapDeliveryNoteError } from '../../delivery-notes/delivery-note-api-errors';
import { GuideLink } from '../../guide/guide-link';
import { formatCalendarDate } from '../../orders/format-calendar-date';
import { DeliveryNoteItemsTab } from './components/delivery-note-items-tab';
import { DeliveryNoteSignatureTab } from './components/delivery-note-signature-tab';
import { SignOnScreenModal } from './components/sign-on-screen-modal';
import { UploadSignedModal } from './components/upload-signed-modal';

export type DeliveryNoteDetailTab = 'items' | 'firma';

const deliveryNoteDetailRoute = getRouteApi('/protected/delivery-notes/$id');

const TAB_LABELS: Record<DeliveryNoteDetailTab, string> = {
  items: 'Ítems',
  firma: 'Firma',
};

const TAB_ICONS: Record<DeliveryNoteDetailTab, typeof ClipboardList> = {
  items: ClipboardList,
  firma: FileSignature,
};

/**
 * `delivery-note-documents/spec.md` → "Pantalla de detalle de remito en
 * back-office". Transition actions are gated by permission, same criterion
 * `order-detail-page.tsx` applies for its own `hasPermission` checks
 * (`design.md` §9.2). "Cargar escaneo" is added in PR 7.
 */
export function DeliveryNoteDetailPage(): JSX.Element {
  const { id } = deliveryNoteDetailRoute.useParams();
  const [tab, setTab] = useState<DeliveryNoteDetailTab>('items');
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currentUser = useCurrentUser();

  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [signModalOpen, setSignModalOpen] = useState(false);

  const query = useQuery({ queryKey: ['delivery-note', id], queryFn: () => getDeliveryNote(id) });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['delivery-note', id] });
    await queryClient.invalidateQueries({ queryKey: ['delivery-notes'] });
  }

  const dispatchMutation = useMutation({
    mutationFn: () => dispatchDeliveryNote(id),
    onSuccess: async () => {
      await invalidate();
      toast('success', 'Remito despachado');
    },
  });

  const deliverMutation = useMutation({
    mutationFn: () => markDeliveryNoteDelivered(id),
    onSuccess: async () => {
      await invalidate();
      toast('success', 'Entrega registrada');
    },
  });

  const voidMutation = useMutation({
    mutationFn: (reason: string) => voidDeliveryNote(id, reason),
    onSuccess: async () => {
      await invalidate();
      setVoidModalOpen(false);
      setVoidReason('');
      toast('success', 'Remito anulado');
    },
  });

  const [downloadError, setDownloadError] = useState<string | undefined>(undefined);
  const [downloading, setDownloading] = useState(false);

  async function handleDownloadPdf(noteNumber: string): Promise<void> {
    setDownloadError(undefined);
    setDownloading(true);

    try {
      await downloadDeliveryNotePdf(id, noteNumber);
    } catch {
      setDownloadError('No pudimos descargar el PDF del remito.');
    } finally {
      setDownloading(false);
    }
  }

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) {
      return (
        <Alert tone="danger" title="Remito no encontrado">
          <Link to="/delivery-notes" className="underline">
            Volver al listado
          </Link>
        </Alert>
      );
    }

    return (
      <Alert tone="danger" title="No pudimos cargar el remito">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  const note = query.data;
  const canUpdate = hasPermission(currentUser.data, 'deliverynote.update');
  const canVoid = hasPermission(currentUser.data, 'deliverynote.void');
  const canExport = hasPermission(currentUser.data, 'deliverynote.export');
  const canUpload = hasPermission(currentUser.data, 'deliverynote.upload');
  const canSign = hasPermission(currentUser.data, 'deliverynote.sign');
  const canVoidNote = canVoid && note.status !== 'INVOICED' && note.status !== 'VOID';

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Truck}
        title={note.noteNumber}
        description={`${note.customerNameSnapshot} · ${note.siteNameSnapshot} · ${formatCalendarDate(note.serviceDate)}`}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge domain="deliverynote" status={note.status} />
            <GuideLink moduleId="delivery-notes" />
          </div>
        }
      />

      {note.status === 'VOID' ? (
        <Alert tone="danger" title="Remito anulado">
          {note.voidReason ?? 'Sin motivo registrado.'}
        </Alert>
      ) : null}

      {dispatchMutation.isError ? (
        <Alert tone="danger">{mapDeliveryNoteError(dispatchMutation.error)}</Alert>
      ) : null}
      {deliverMutation.isError ? (
        <Alert tone="danger">{mapDeliveryNoteError(deliverMutation.error)}</Alert>
      ) : null}
      {downloadError !== undefined ? <Alert tone="danger">{downloadError}</Alert> : null}

      <Tabs value={tab} onValueChange={(value) => setTab(value as DeliveryNoteDetailTab)}>
        <TabList aria-label="Pestañas del remito">
          {(Object.keys(TAB_LABELS) as DeliveryNoteDetailTab[]).map((key) => (
            <Tab key={key} value={key} icon={TAB_ICONS[key]}>
              {TAB_LABELS[key]}
            </Tab>
          ))}
        </TabList>

        <TabPanel value="items">
          <DeliveryNoteItemsTab note={note} />
        </TabPanel>

        <TabPanel value="firma">
          <DeliveryNoteSignatureTab note={note} />
        </TabPanel>
      </Tabs>

      <div className="flex flex-wrap items-center gap-3">
        {note.status === 'PENDING' && canUpdate ? (
          <Button
            icon={Send}
            loading={dispatchMutation.isPending}
            onClick={() => dispatchMutation.mutate()}
          >
            Despachar
          </Button>
        ) : null}
        {note.status === 'DISPATCHED' && canUpdate ? (
          <Button
            icon={Send}
            loading={deliverMutation.isPending}
            onClick={() => deliverMutation.mutate()}
          >
            Registrar entrega
          </Button>
        ) : null}
        {canVoidNote ? (
          <Button variant="secondary" icon={XCircle} onClick={() => setVoidModalOpen(true)}>
            Anular
          </Button>
        ) : null}
        {canExport ? (
          <Button
            variant="secondary"
            icon={Download}
            loading={downloading}
            onClick={() => void handleDownloadPdf(note.noteNumber)}
          >
            Descargar PDF
          </Button>
        ) : null}
        {note.status === 'DELIVERED' && canUpload ? (
          <Button variant="secondary" icon={Upload} onClick={() => setUploadModalOpen(true)}>
            Cargar escaneo
          </Button>
        ) : null}
        {note.status === 'DELIVERED' && canSign ? (
          <Button variant="secondary" icon={PenLine} onClick={() => setSignModalOpen(true)}>
            Firmar en pantalla
          </Button>
        ) : null}
        {note.status === 'DISPATCHED' && canSign ? (
          <Button variant="secondary" icon={PenLine} onClick={() => setSignModalOpen(true)}>
            Registrar entrega y firma
          </Button>
        ) : null}
      </div>

      {note.status === 'DISPATCHED' && canUpdate ? (
        <p className="-mt-3 text-sm text-slate-500">
          Al registrar la entrega, el remito pasa a Entregado. Esto no se puede deshacer.
        </p>
      ) : null}

      {voidModalOpen ? (
        <Modal
          open
          onClose={() => setVoidModalOpen(false)}
          title="Anular remito"
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {voidMutation.isError ? (
              <Alert tone="danger">{mapDeliveryNoteError(voidMutation.error)}</Alert>
            ) : null}
            <Alert tone="warning">
              Si el remito ya estaba firmado, se pierde la firma. El pedido vuelve a Confirmado y
              podés generar un remito nuevo. Esta acción no se puede deshacer.
            </Alert>
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-slate-900">Motivo de la anulación</span>
              <textarea
                name="voidReason"
                required
                value={voidReason}
                onChange={(event) => setVoidReason(event.target.value)}
                className="min-h-24 rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <div className="flex items-center gap-3">
              <Button
                loading={voidMutation.isPending}
                disabled={voidReason.trim() === ''}
                onClick={() => voidMutation.mutate(voidReason)}
              >
                Confirmar anulación
              </Button>
              <Button variant="secondary" onClick={() => setVoidModalOpen(false)}>
                Volver
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {uploadModalOpen ? (
        <UploadSignedModal
          note={note}
          onClose={() => setUploadModalOpen(false)}
          onUploaded={() => {
            setUploadModalOpen(false);
            void invalidate();
            toast('success', 'Remito firmado cargado');
          }}
        />
      ) : null}

      {signModalOpen ? (
        <SignOnScreenModal
          note={note}
          onClose={() => setSignModalOpen(false)}
          onSigned={() => {
            setSignModalOpen(false);
            void invalidate();
            toast('success', 'Remito firmado');
          }}
        />
      ) : null}
    </section>
  );
}
